import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef, useState } from 'react'
import { useLanguage } from '../contexts/LanguageContext'

const Features = () => {
  const { t } = useLanguage()
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.2 })
  const getCategoryData = (categoryId, mode = 'busy') => {
    if (!categoryId) return null
    const modeKey = mode === 'busy' ? 'busy' : 'travel'
    return {
      why: t.features.why[categoryId],
      tasks: [
        { id: 1, text: t.features.done[categoryId][modeKey].task1, completed: categoryId === 'sleep' && mode === 'busy' },
        { id: 2, text: t.features.done[categoryId][modeKey].task2, completed: false },
        { id: 3, text: t.features.done[categoryId][modeKey].task3, completed: false },
      ],
    }
  }

  const [adaptMode, setAdaptMode] = useState('busy')
  const [selectedCategory, setSelectedCategory] = useState('sleep')
  const [tasks, setTasks] = useState(() => {
    const initialData = getCategoryData('sleep', 'busy')
    return initialData ? initialData.tasks : []
  })

  const todayCategories = [
    { id: 'sleep', label: t.features.today.sleep, icon: '🌙' },
    { id: 'food', label: t.features.today.food, icon: '🍽️' },
    { id: 'movement', label: t.features.today.movement, icon: '🏃' },
  ]

  const currentData = getCategoryData(selectedCategory, adaptMode) || getCategoryData('sleep', adaptMode)

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId)
    const newData = getCategoryData(categoryId, adaptMode)
    if (newData) {
      setTasks(newData.tasks)
    }
  }

  const handleAdaptModeChange = (mode) => {
    setAdaptMode(mode)
    const newData = getCategoryData(selectedCategory, mode)
    if (newData) {
      setTasks(newData.tasks)
    }
  }

  const toggleTask = (taskId) => {
    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId ? { ...task, completed: !task.completed } : task
      )
    )
  }

  return (
    <section id="features" ref={ref} className="min-h-screen py-12 md:py-16 lg:py-24 xl:py-32 bg-gradient-to-b from-white via-emerald-50/30 to-white relative overflow-hidden flex items-center">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-32 bg-gradient-to-b from-transparent via-primary to-transparent" />

      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 md:mb-12 lg:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 md:mb-5 lg:mb-6 text-gray-900 leading-tight">
            {t.features.title}
          </h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
            {t.features.subtitle}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden premium-shadow">
            <div className="p-6 md:p-8 lg:p-10">
              <div className="mb-8">
                <h3 className="text-xs md:text-sm font-bold text-gray-400 uppercase tracking-widest mb-6">
                  {t.features.cardTitle}
                </h3>

                <div className="space-y-8">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      <h4 className="text-sm md:text-base font-bold text-primary uppercase tracking-wide">
                        {t.features.sections.today}
                      </h4>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {todayCategories.map((category, index) => (
                        <motion.button
                          key={category.id}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={isInView ? { opacity: 1, scale: 1 } : {}}
                          transition={{ delay: 0.5 + index * 0.1 }}
                          onClick={() => handleCategoryChange(category.id)}
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                          className={`px-4 md:px-6 py-2.5 md:py-3 rounded-xl font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transition-all duration-300 ${
                            selectedCategory === category.id
                              ? 'gradient-bg text-white ring-2 ring-primary ring-offset-2'
                              : 'gradient-bg text-white'
                          }`}
                        >
                          {category.label}
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.7 }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      <h4 className="text-sm md:text-base font-bold text-primary uppercase tracking-wide">
                        {t.features.sections.why}
                      </h4>
                    </div>
                    <motion.div
                      key={`${selectedCategory}-${adaptMode}`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                      className="relative pl-6 border-l-2 border-gray-200"
                    >
                      <p className="text-sm md:text-base text-gray-700 leading-relaxed italic">
                        {currentData.why}
                      </p>
                    </motion.div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.9 }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      <h4 className="text-sm md:text-base font-bold text-primary uppercase tracking-wide">
                        {t.features.sections.done}
                      </h4>
                    </div>
                    <motion.div
                      key={`${selectedCategory}-${adaptMode}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-3"
                    >
                      {tasks.map((task, index) => (
                        <motion.button
                          key={task.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          onClick={() => toggleTask(task.id)}
                          whileHover={{ scale: 1.02, x: 4 }}
                          whileTap={{ scale: 0.98 }}
                          className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left cursor-pointer group"
                        >
                          <motion.div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                              task.completed
                                ? 'bg-primary border-primary'
                                : 'bg-white border-gray-300 group-hover:border-primary'
                            }`}
                            animate={{
                              scale: task.completed ? [1, 1.1, 1] : 1,
                            }}
                            transition={{ duration: 0.3 }}
                          >
                            {task.completed && (
                              <motion.svg
                                initial={{ scale: 0, rotate: -180 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                                className="w-3 h-3 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </motion.svg>
                            )}
                          </motion.div>
                          <span
                            className={`text-sm md:text-base transition-all ${
                              task.completed
                                ? 'text-gray-600 line-through'
                                : 'text-gray-900 group-hover:text-primary'
                            }`}
                          >
                            {task.text}
                          </span>
                        </motion.button>
                      ))}
                    </motion.div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 1.3 }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      <h4 className="text-sm md:text-base font-bold text-primary uppercase tracking-wide">
                        {t.features.sections.adapt}
                      </h4>
                    </div>
                    <div className="flex flex-col gap-3">
                      <div className="flex gap-2">
                        <motion.button
                          onClick={() => handleAdaptModeChange('busy')}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`px-4 md:px-6 py-2.5 md:py-3 rounded-xl font-semibold text-sm md:text-base transition-all duration-300 ${
                            adaptMode === 'busy'
                              ? 'gradient-bg text-white shadow-lg'
                              : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {t.features.adapt.busy}
                        </motion.button>
                        <motion.button
                          onClick={() => handleAdaptModeChange('travel')}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`px-4 md:px-6 py-2.5 md:py-3 rounded-xl font-semibold text-sm md:text-base transition-all duration-300 ${
                            adaptMode === 'travel'
                              ? 'gradient-bg text-white shadow-lg'
                              : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {t.features.adapt.travel}
                        </motion.button>
                      </div>
                      <p className="text-xs md:text-sm text-gray-500 italic">
                        {t.features.adapt.note}
                      </p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Features
